import React, { useState, useEffect, useRef } from "react"
import _get from "lodash/get"
import { useDispatch, useSelector } from "react-redux"

import { getConfig } from "@/constants/config"
import { getShortenedUrl, sanitizeInnerHtml, resetUrl } from "@/utils/helper"
import { getTotalTabCount } from "@/utils/tabCountHelper"
import {
  setSearchCount,
  setSearchFilter,
  searchState,
  setActiveTabDispatch,
} from "@/store/features/searchSlice"
import { profileBrandTypeahead } from "@/store/features/productListSlice"
import dynamic from "next/dynamic"
const PopularSearch = dynamic(() =>
  import("@/components/Default/DynamicSearch/v1/PopularSearch")
)
const NoResultLinks = dynamic(() =>
  import("@/components/Default/DynamicSearch/v1/NoResultLinks")
)

import SearchProgressive from "@/components/Search/v1/SearchProgressive"
import searchWrapperStyle from "@/components/Search/v1/SearchView/index.module.scss"
import { SearchResults } from "@kohler-dsm-library/kohler-modules"

const SearchComponentView = props => {
  const {
    data,
    brand = "",
    loadPopularSearch,
    activeTabVal,
    tabList,
    searchKeyword,
    staticTexts,
    totalResults,
    onSearch,
    loading,
    searchResponse,
    isSterling = false,
  } = props
  const {
    home = "",
    searchResults = "",
    searchBrand = "",
    noResults = "",
    searchInstead = "",
    noResultsSuggest = "",
    ariaLabel = "",
  } = staticTexts
  const dispatch = useDispatch()
  const searchRef = useRef(null)
  const newState = useSelector(searchState)
  const {
    noResultPopularTopicsHeading = "",
    noResultPopularLinks = [],
    noResultOurSuggestionsHeading = "",
    noResultOurSuggestionsSubcopy = "",
  } = data

  const [activeTab, setActiveTab] = useState(
    activeTabVal
      ? activeTabVal < 0
        ? 0
        : activeTabVal
      : newState?.searchDetails?.activeTab
  ) // while refresh keep select the same tab
  const [homePagePath, setHomePagePath] = useState("")
  const [searchPlaceholder, setSearchplaceholder] = useState(brand)
  const [inputValueChange, setInputValueChange] = useState(searchKeyword || "")
  const [typeAheadResults, setTypeaheadResults] = useState([])
  const [forceTabLoader, setForceTabLoader] = useState(false)
  const [refreshTrigger, setRefreshTrigger] = useState(0)

  useEffect(() => {
    setInputValueChange(newState?.searchDetails?.keyword)
  }, [newState?.searchDetails?.keyword])

  let isSpellTagged = false
  let resultsText = ""
  let keepSearching = ""
  let currTab = activeTab ? Number(activeTab) : 0 // required for ADA focus

  // Calculate total count from all tabs
  const calculatedTotalResults = getTotalTabCount(
    tabList,
    newState?.searchDetails
  )

  const setCount = () => {
    const counts = {}
    tabList?.forEach(tab => {
      counts[tab?.id] = {
        count: tab?.count,
      }
      if (searchKeyword !== newState?.searchDetails?.keyword) {
        dispatch(setSearchFilter({ queries: [], key: tab?.id }))
      }
    })
    dispatch(setSearchCount(counts))
  }

  useEffect(() => {
    // Will trigger only when tab keyword is available in url.
    if (activeTabVal !== undefined) {
      setActiveTab(activeTabVal)
    }
  }, [activeTabVal])

  useEffect(() => {
    getConfig()?.then(config => {
      const searchBrandPlaceholder =
        config?.apiEndPointData?.map?.searchBrandPlaceholder ?? brand
      if (searchBrandPlaceholder?.length > 0) {
        setSearchplaceholder(searchBrandPlaceholder)
      }
      getShortenedUrl(config?.general?.homepageUrl ?? "/")?.then(res =>
        setHomePagePath(res)
      )
    })

    setCount()
  }, [])
  useEffect(() => {
    if (loading) {
      setCount()
    }
  }, [searchKeyword, loading])
  useEffect(() => {
    if (activeTab < 0) {
      setActiveTab(0)
    }
    if (tabList?.length <= (activeTab ? Number(activeTab) : 0)) {
      setActiveTab(0)
    }
  }, [activeTab, tabList])

  if (
    searchResponse?.data?.fusion?.tagger &&
    searchResponse?.data?.fusion?.tagger[0] &&
    searchResponse?.data?.fusion?.tagger[0]?.tag?.type === "spell"
  ) {
    isSpellTagged = true
    keepSearching = searchResponse?.data?.fusion?.tagger[0]?.surfaceForm
    resultsText = `${
      staticTexts?.showing
    } ${calculatedTotalResults?.toLocaleString()} ${staticTexts?.results} ${
      staticTexts?.for
    } "${searchResponse?.data?.fusion?.tagger[0]?.outputForms[0]}"`
  } else if (
    searchResponse?.data?.fusion?.autoCorrectQuery &&
    searchResponse?.data?.fusion?.org_q &&
    !isSterling
  ) {
    isSpellTagged = true
    keepSearching = searchResponse?.data?.fusion?.org_q
    resultsText = `${
      staticTexts?.showing
    } ${calculatedTotalResults?.toLocaleString()} ${staticTexts?.results} ${
      staticTexts?.for
    } "${searchResponse?.data?.fusion?.autoCorrectQuery}"`
  } else {
    resultsText = `${
      staticTexts?.showing
    } ${calculatedTotalResults?.toLocaleString()} ${staticTexts?.results} ${
      staticTexts?.for
    } "${searchKeyword}"`
  }

  const changeTab = tab => {
    // Check if clicking the already active tab
    if (tab === activeTab) {
      setForceTabLoader(true)
      setActiveTab(tab)
      setRefreshTrigger(prev => prev + 1)
      dispatch(setActiveTabDispatch(tab))
      setTimeout(() => {
        setForceTabLoader(false)
      }, 750)
      return // Exit early, no need to update state or URL
    }
    setActiveTab(tab)
    dispatch(setActiveTabDispatch(tab))

    // Force show TabLoader for 750ms on tab change
    setForceTabLoader(true)
    setTimeout(() => {
      setForceTabLoader(false)
    }, 750)

    const query = window?.location?.search
    const currentTab = tabList[tab]
    if (sessionStorage?.getItem("SRPTabDetails")) {
      const srpTabDetails = JSON.parse(sessionStorage?.getItem("SRPTabDetails"))
      const storedUrl = srpTabDetails[currentTab?.id]

      if (storedUrl) {
        // Add the correct tab parameter for the active tab
        const urlParams = new URLSearchParams(storedUrl.replace("?", ""))
        urlParams.set("tab", currentTab?.id)
        const urlWithCorrectTab = `?${urlParams.toString()}`

        // Debounce tab URL updates to prevent Safari crashes
        if (window.__tabUrlUpdateTimeout) {
          clearTimeout(window.__tabUrlUpdateTimeout)
        }
        window.__tabUrlUpdateTimeout = setTimeout(() => {
          try {
            history?.replaceState(
              window?.location?.pathname,
              document?.title,
              urlWithCorrectTab
            )
          } catch (err) {
            // eslint-disable-next-line no-console
            console.warn("Failed to update tab URL:", err.message)
          }
        }, 100) // 100ms debounce for tab switches
      } else resetUrl()
    } else if (query) resetUrl()

    if (
      newState?.searchDetails?.keyword &&
      newState?.searchDetails?.keyword !== searchKeyword
    ) {
      setCount()
    }
    if (searchKeyword === "") {
      const urlParams = new URLSearchParams(window?.location?.search)
      const keyword = urlParams?.get("keyword")
      onSearch(keyword)
    }
  }
  // In your tab content component (Products/Articles/etc.)
  useEffect(() => {
    // This will trigger when refreshTrigger changes
    if (refreshTrigger) onSearch(searchKeyword)
  }, [refreshTrigger])

  const handleSearchEnterNew = () => {
    onSearch(inputValueChange)
  }

  const handleKeyPress = e => {
    if (e.keyCode === 37 || e.keyCode === 39) {
      if (e.keyCode === 37) {
        currTab = currTab - 1
      } else if (e.keyCode === 39) {
        currTab = currTab + 1
      }
      if (currTab < 0) currTab = tabList?.length - 1
      if (currTab >= tabList?.length) currTab = 0
      document.getElementById(`${tabList[currTab]?.title}-tab`)?.focus()
    }
  }

  useEffect(() => {
    const fetchTypeahead = async () => {
      try {
        const response = await dispatch(
          profileBrandTypeahead([inputValueChange, brand.toLowerCase()])
        )

        const transformedResults =
          response?.payload?.response?.docs?.map(doc => ({
            label: doc.productName_s,
            value: doc.productName_s,
          })) || []
        inputValueChange.length > 2 && setTypeaheadResults(transformedResults)
        if (inputValueChange.length < 3) setTypeaheadResults([])
      } catch (error) {
        console.error("Error fetching typeahead:", error)
      }
    }

    if (inputValueChange) {
      fetchTypeahead()
    }
  }, [inputValueChange, brand, dispatch])

  const handleBreadcrumbClick = e => {
    const target = e.target.closest('[data-datalayer-id="search-breadcrumb"]')
    if (target) {
      const rawEventInfo = JSON.parse(
        target?.attributes?.["data-datalayer-data"]?.nodeValue || "{}"
      )
      const page = window.pageObj || {}

      // Common attributes to add to all events
      const commonEventInfo = {
        eventAction: rawEventInfo.eventAction || "search:breadcrumb",
        eventType: rawEventInfo.eventType || "navigation",
        eventName: rawEventInfo.eventName || "search:breadcrumb",
        internalLinkPosition: rawEventInfo?.internalLinkPosition || "search",
        internalLinkZoneName:
          rawEventInfo?.internalLinkZoneName || "search:search box",
        internalLinkType: rawEventInfo?.internalLinkType || "search:breadcrumb",
      }

      // Merge common attributes with specific event info
      const eventInfo = {
        ...rawEventInfo,
        ...commonEventInfo,
      }

      window.adobeDataLayer = window.adobeDataLayer || []
      window.adobeDataLayer.push({
        event: "cmp:click",
        page,
        eventInfo,
      })
    }
  }

  useEffect(() => {
    document.addEventListener("click", handleBreadcrumbClick)
    return () => document.removeEventListener("click", handleBreadcrumbClick)
  }, [])
  const breadcrumbEventInfo = type => {
    const homeBreadcrumb = type?.toLowerCase() === "home"
    return JSON.stringify({
      eventAction: "search:breadcrumb",
      eventName: "search:breadcrumb",
      eventType: "navigation",
      eventMsg: "n/a",
      eventStatus: "n/a",
      internalLinkName: homeBreadcrumb
        ? `${staticTexts?.home}`
        : `${staticTexts?.home} > ${staticTexts?.searchResults}`,
      internalLinkPosition: "search",
      internalLinkType: "search:breadcrumb",
      internalLinkZoneName: "search:search box",
      internalLinkURL: homeBreadcrumb
        ? `${homePagePath}`
        : `${window?.location?.href}`,
      clickInternalLinks: "true",
    })
  }

  const searchResultsContent = {
    mod: "white",
    breadcrumbs: [
      {
        href: homePagePath,
        text: home,
        trackingAttributes: {
          "data-datalayer-id": "search-breadcrumb",
          "data-datalayer-data": breadcrumbEventInfo(home),
        },
      },
      {
        text: searchResults,
        href: "#",
        trackingAttributes: {
          "data-datalayer-id": "search-breadcrumb",
          "data-datalayer-data": breadcrumbEventInfo(searchResults),
        },
      },
    ],
    searchInputField: {
      id: "search-result-input",
      label: "Search Kohler",
      ref: searchRef,
      value: inputValueChange,
      onChange: e => {
        setInputValueChange(e.target.value)
      },
      onKeyDown: e =>
        (e.key === "Enter" || e.keyCode === 13) && handleSearchEnterNew(),
      placeholder: `${searchBrand} ${searchPlaceholder}`,
      searchCTA: {
        text: "Search",
        onClick: () => onSearch(inputValueChange),
      },
      clearCTA: {
        onClick: () => {
          setInputValueChange("")
        },
      },
      filteredResults: typeAheadResults,
      results: {
        options: typeAheadResults,

        onOptionSubmit: e => {
          onSearch(e.value, inputValueChange, "FROM TYPEAHEAD SUGGESTION")
        },
      },
    },
    copy:
      totalResults > 0
        ? {
            text: loading
              ? `${staticTexts?.loading} ${staticTexts?.results}`
              : totalResults === 0
              ? noResults
              : resultsText,
          }
        : undefined,
    suggestion:
      totalResults > 0
        ? {
            text: !loading &&
              isSpellTagged === true &&
              searchKeyword?.length > 2 && (
                <span className="search-result__instead">
                  {searchInstead}{" "}
                  <span onClick={() => onSearch(keepSearching, true, true)}>
                    {keepSearching}
                  </span>
                </span>
              ),
          }
        : undefined,
  }

  return (
    <div>
      <div
        className={`${searchWrapperStyle?.searchViewStyleWrapper} Plp-Search`}
      >
        <div className="search-result">
          <div className="container-fluid">
            <div className="product-listing__row no-gutters">
              <div
                className={`product-listing__col search-result__breadcrumb-header ${
                  totalResults === 0 ? " --no-results" : ""
                }`}
              >
                <div className="search-landing">
                  <SearchResults {...searchResultsContent} />
                  <div className="search-result__description search-no-resultes">
                    <div
                      role="alert"
                      aria-live="polite"
                      className={`search-result__showing ${
                        totalResults === 0 ? " --no-results" : ""
                      }`}
                    >
                      {totalResults === 0 ? (
                        <div className="search-result__no-results">
                          <p>{noResults}</p>
                          <p>{noResultsSuggest}</p>
                        </div>
                      ) : (
                        ""
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {loading ? (
              <SearchProgressive />
            ) : (
              <div className="row no-gutters">
                {!loading &&
                  (totalResults > 0 ? (
                    <div className="col-12">
                      <div className="search-result__tab-header">
                        <div
                          className="container search-result__tab-buttons kf-react-container"
                          role="tablist"
                        >
                          <div className="d-flex">
                            {tabList?.map((tab, i) => {
                              return (
                                <button
                                  key={i}
                                  id={`${tab?.title}-tab`}
                                  aria-label={ariaLabel?.tabs(
                                    newState?.searchDetails[tab?.id]?.count,
                                    tab?.title
                                  )}
                                  aria-selected={
                                    (activeTab ? Number(activeTab) : 0) === i
                                      ? true
                                      : false
                                  }
                                  role="tab"
                                  data-gbh-data-layer={`{"eventAction":"search results:hero:inner tabs","eventName":"search results:hero:inner tabs","eventType":"navigation","eventMsg":"n/a","eventStatus":"n/a","internalLinkName":"${tab?.title}","internalLinkPosition":"search","internalLinkType":"search results:navigation","internalLinkZoneName":"search results:hero","internalLinkURL":"n/a","clickInternalLinks":"true"}`}
                                  className={`search-result__tab-button gbh-data-layer ${
                                    (activeTab ? Number(activeTab) : 0) === i
                                      ? "--active"
                                      : ""
                                  }`}
                                  onClick={() => changeTab(i)}
                                  onKeyDown={e => handleKeyPress(e)}
                                  tabIndex={
                                    (activeTab ? Number(activeTab) : 0) === i
                                      ? 0
                                      : -1
                                  }
                                >
                                  <span role="heading" aria-level="2">{`${
                                    tab?.title
                                  } (${
                                    newState?.searchDetails[tab?.id]?.count
                                  })`}</span>
                                </button>
                              )
                            })}
                          </div>
                        </div>
                      </div>
                      <div className="search-result__tab-content">
                        {React.cloneElement(
                          _get(
                            tabList[activeTab ? Number(activeTab) : 0],
                            "content",
                            ""
                          ),
                          { forceTabLoader, refreshTrigger }
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="col-12">
                      <div className="search-result__no-result">
                        <div className="row">
                          <div className="col-lg-4 col-sm-12 search-result__no-result-column">
                            <h2 className="search-result__suggestions-title wow animate__slideInLeft">
                              {noResultOurSuggestionsHeading}
                            </h2>
                            <div className="search-result__suggestions-list wow animate__fadeInUp">
                              {sanitizeInnerHtml(noResultOurSuggestionsSubcopy)}
                            </div>
                          </div>
                          <div className="col-lg-4 col-sm-12 search-result__no-result-column">
                            <PopularSearch
                              staticTexts={staticTexts}
                              loadPopularSearch={loadPopularSearch}
                              brand={brand}
                              updateFromResult={onSearch}
                              staticData={data}
                            />
                          </div>
                          <div className="col-lg-4 col-sm-12 search-result__no-result-column">
                            <NoResultLinks
                              data={noResultPopularLinks}
                              link
                              heading={noResultPopularTopicsHeading}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default SearchComponentView
